/** Figma Dashboard / Kintsugi Advisor — neutral and layout tokens (node 10933:72139). */
/**
 * A portfolio with branding on sets the `--ks-colors-brand-*` variables on the document root.
 * The Kin panel reads them so it follows the brand. The fallback is the Kintsugi value, so an app
 * with no brand (the main app, admin, or a portfolio with branding off) renders exactly as before.
 */
function brandColor(step: 50 | 100 | 200 | 300 | 500 | 600 | 700, fallback: string): string {
  return `var(--ks-colors-brand-${step}, ${fallback})`;
}

/**
 * Document-root variable that holds the generic green for a success mark while branding is on.
 * A success mark keeps its meaning under any brand, so it never reads a `brand-*` variable.
 * Unset, the mark keeps the Kintsugi green.
 */
export const ADVISOR_STATUS_SUCCESS_CSS_VARIABLE = "--ks-advisor-status-success";
export const ADVISOR_STATUS_SUCCESS = `var(${ADVISOR_STATUS_SUCCESS_CSS_VARIABLE}, #396862)`;
/**
 * Kin Chatbot — assistant reply fill (Figma [13101-69655](https://www.figma.com/design/bYyIivHby22hQ1jTAxVnHV/Kin-Chatbot?node-id=13101-69655); same swatch as legacy node 13101-70068).
 */
export const ADVISOR_ASSISTANT_BUBBLE_BG = "#F4F5F7";
/**
 * Legacy soft shadow — the Kin AI Answer plate in Figma (13101:69655) is flat (no drop shadow). Prefer no shadow on ``AdvisorAssistantMarkdown``.
 */
export const ADVISOR_ASSISTANT_BUBBLE_BOX_SHADOW =
  "0 2px 8px rgba(23, 24, 24, 0.05), 0 4px 14px rgba(23, 24, 24, 0.04)";
/**
 * Kin Chatbot — user message bubble fill, Figma Primary/700 (KG) ([User question — 13101-70065](https://www.figma.com/design/bYyIivHby22hQ1jTAxVnHV/Kin-Chatbot?node-id=13101-70065)).
 */
export const ADVISOR_USER_BUBBLE_BG = brandColor(700, "#213331");
export const ADVISOR_PANEL_NEUTRAL_100 = "#E7E7E7";
export const ADVISOR_PANEL_NEUTRAL_200 = "#D1D1D1";
export const ADVISOR_PANEL_NEUTRAL_400 = "#3D3D3D";
export const ADVISOR_PANEL_NEUTRAL_250 = "#888888";
export const ADVISOR_PANEL_NEUTRAL_50_BG = "#F6F6F6";
// Fill only (avatar, button background): `brand-500` is the raw primary, so it is too pale for text on white.
export const ADVISOR_PANEL_PRIMARY_500 = brandColor(500, "#396862");
// Text, borders, outlines and strokes on a light surface: a pale primary is darkened until it reads at 4.5:1 on white.
export const ADVISOR_PANEL_PRIMARY_TEXT = "var(--ks-colors-brand-text, #396862)";
// Label and icon color on a primary fill: a pale primary swaps white for a dark label.
export const ADVISOR_PANEL_ON_PRIMARY = "var(--ks-colors-brand-contrast, white)";
/**
 * Document-root variable that holds the link hover shade while branding is on.
 * The main app also defines `brand-600` and `brand-700`, so a brand-token fallback would change its hover color.
 */
export const ADVISOR_LINK_HOVER_CSS_VARIABLE = "--ks-advisor-link-hover";
/** Hover shade for inline links in assistant markdown: the brand palette's `textOnWhiteHover`, or `#2A4542` (KDS `primary.600`) when branding is off. */
export const ADVISOR_PANEL_PRIMARY_TEXT_HOVER = `var(${ADVISOR_LINK_HOVER_CSS_VARIABLE}, #2A4542)`;

export const ADVISOR_PANEL_PRIMARY_50 = brandColor(50, "#F4F9F8");
/**
 * Soft Kin mint — lead thinking-dot + active tool-progress glyph.
 * Matches the AI answering accent used beside the Kin avatar (not Primary/500).
 */
export const ADVISOR_KIN_MINT_400 = brandColor(300, "#8BBDB3");
/** Softer mint steps for the three thinking dots (after {@link ADVISOR_KIN_MINT_400}). */
export const ADVISOR_KIN_MINT_200 = brandColor(200, "#B7D8D1");
export const ADVISOR_KIN_MINT_100 = brandColor(100, "#DBECE7");
/** Matches agent ``ChatFeedbackV2Request.comment`` max_length (kintsugi-agent). */
export const ADVISOR_MESSAGE_FEEDBACK_COMMENT_MAX_LENGTH = 2000;
/**
 * Figma “Popup Shadow” — RHS drawer chrome and the **inline thumbs-down written-feedback card**
 * (Kin “Not helpful”, frame `13191:23818`). Use via `AgentAssistantWrittenFeedbackInline` only — do not fork the shadow string in app shells.
 */
export const ADVISOR_PANEL_BOX_SHADOW =
  "0 8px 32px -4px rgba(23, 24, 24, 0.12), 0 -2px 26px -3px rgba(23, 24, 24, 0.08)";

export interface AdvisorBrandVariablesInput {
  /** The brand's text-on-white hover shade. */
  linkHover: string;
  /** The generic status green. */
  statusSuccess: string;
}

/** The document-root variables the Kin panel reads while branding is on. Both apps share them. */
export function advisorBrandVariables({
  linkHover,
  statusSuccess,
}: AdvisorBrandVariablesInput): readonly (readonly [string, string])[] {
  return [
    // A success mark is a status, so it takes the generic green, not a brand shade.
    [ADVISOR_STATUS_SUCCESS_CSS_VARIABLE, statusSuccess],
    // A hovered link must not look lighter than a resting one (`brand-text`), and must keep reading on white.
    [ADVISOR_LINK_HOVER_CSS_VARIABLE, linkHover],
  ];
}
